import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type SyntheticEvent,
} from "react";
import ReactCrop, {
  convertToPixelCrop,
  type PercentCrop,
  type PixelCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useTranslation } from "react-i18next";
import { useTheme } from "@repo/tokens";
import { Box, Text, Icon, Modal, Radio, RadioGroup } from "@repo/ui";
export type DoubtsSubjectOption = { subjectId: number; name: string };
import {
  downscaleBlobIfNeeded,
  getCroppedImageBlobFromPixelCrop,
  rotateImageToBlobUrl,
} from "./cropImage";
import cropTipUrl from "../../../../assets/instructions/crop-tip.svg";
import leftRotateUrl from "../../../../assets/instructions/left-rotate.svg";
import rightRotateUrl from "../../../../assets/instructions/right-rotate.svg";

export type ImageUploadCompletePayload = {
  blob: Blob;
  previewObjectUrl: string;
  subjectId: number;
  subjectName: string;
};

type Props = {
  isOpen: boolean;
  imageUrl: string;
  onCancel: () => void;
  /** Called when user applies subject (end of browse → crop → preview → subject). May return a Promise (OCR + create doubt). */
  onComplete: (payload: ImageUploadCompletePayload) => void | Promise<void>;
  subjects: DoubtsSubjectOption[];
  subjectsLoading: boolean;
  subjectsError: boolean;
  sessionSubjectsReady: boolean;
  onRetrySubjects?: () => void;
  /**
   * Hosts (e.g. AINA + LiveKit) may render full-screen overlays with very high z-index;
   * portaled modals must stack above them to stay interactive.
   */
  overlayZIndex?: number;
};

const MAX_OUTPUT_EDGE = 800;

const DEFAULT_CROP_PCT = {
  unit: "%" as const,
  x: 0,
  y: 0,
  width: 100,
  height: 100,
};

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;

/** Pinch: Chrome/Edge often send ctrl+wheel; some engines use deltaZ; Alt+wheel is an explicit fallback. */
function wheelEventRequestsImageZoom(e: WheelEvent) {
  return e.ctrlKey || e.metaKey || e.altKey || Math.abs(e.deltaZ) > 1e-3;
}

function revokeList(urls: string[]) {
  urls.forEach((u) => URL.revokeObjectURL(u));
}

type AdjustPhase = "adjust" | "preview" | "subject";

export function AdjustBoxModal({
  isOpen,
  imageUrl,
  onCancel,
  onComplete,
  subjects,
  subjectsLoading,
  subjectsError,
  sessionSubjectsReady,
  onRetrySubjects,
  overlayZIndex = 2000,
}: Props) {
  const { t } = useTranslation("doubts");
  const { theme } = useTheme();
  const imgRef = useRef<HTMLImageElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const ownedBlobUrlsRef = useRef<string[]>([]);
  const workingSrcRef = useRef(imageUrl);
  const previewCommittedRef = useRef(false);
  const previewUrlRef = useRef<string | null>(null);

  const [phase, setPhase] = useState<AdjustPhase>("adjust");
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [workingSrc, setWorkingSrc] = useState(imageUrl);
  const [crop, setCrop] = useState<PercentCrop>();
  const [completedPixelCrop, setCompletedPixelCrop] =
    useState<PixelCrop | null>(null);
  const [exporting, setExporting] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [baseFitWidth, setBaseFitWidth] = useState<number | null>(null);
  const [selectedSubject, setSelectedSubject] = useState("");
  /** Local so Apply shows "Processing" immediately; parent setState can batch too late for async OCR/create. */
  const [subjectApplyPending, setSubjectApplyPending] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSubjectApplyPending(false);
      revokeList(ownedBlobUrlsRef.current);
      ownedBlobUrlsRef.current = [];
      return;
    }
    previewCommittedRef.current = false;
    revokeList(ownedBlobUrlsRef.current);
    ownedBlobUrlsRef.current = [];
    setPhase("adjust");
    setSelectedSubject("");
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPreviewBlob(null);
    setWorkingSrc(imageUrl);
    setCrop(undefined);
    setCompletedPixelCrop(null);
    setExporting(false);
    setZoom(1);
    setBaseFitWidth(null);
  }, [isOpen, imageUrl]);

  previewUrlRef.current = previewUrl;

  useEffect(() => {
    if (isOpen) return;
    if (!previewCommittedRef.current && previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, [isOpen]);

  workingSrcRef.current = workingSrc;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const measureBaseFit = useCallback(() => {
    const inner = innerRef.current;
    const img = imgRef.current;
    if (!inner || !img?.naturalWidth) return;
    const cs = getComputedStyle(inner);
    const padX =
      (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    const padY =
      (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const maxW = Math.max(1, inner.clientWidth - padX);
    const maxH = Math.max(1, inner.clientHeight - padY);
    const scale = Math.min(
      1,
      maxW / img.naturalWidth,
      maxH / img.naturalHeight,
    );
    setBaseFitWidth(Math.max(1, img.naturalWidth * scale));
  }, []);

  const applyDefaultCropFromImg = useCallback((img: HTMLImageElement) => {
    const { width, height } = img;
    if (width === 0 || height === 0) return;
    const next = { ...DEFAULT_CROP_PCT };
    setCrop(next);
    setCompletedPixelCrop(convertToPixelCrop(next, width, height));
  }, []);

  const onImageLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      requestAnimationFrame(() => {
        measureBaseFit();
        applyDefaultCropFromImg(img);
      });
    },
    [applyDefaultCropFromImg, measureBaseFit],
  );

  useLayoutEffect(() => {
    if (!isOpen || phase !== "adjust") return;
    const inner = innerRef.current;
    if (!inner) return;

    const ro = new ResizeObserver(() => {
      measureBaseFit();
    });
    ro.observe(inner);
    measureBaseFit();

    const onWheel = (e: WheelEvent) => {
      if (!wheelEventRequestsImageZoom(e)) return;
      e.preventDefault();
      e.stopPropagation();
      let factor: number;
      if (Math.abs(e.deltaZ) > 1e-3) {
        factor = Math.exp(-e.deltaZ * 0.035);
      } else {
        const deltaY =
          e.deltaMode === WheelEvent.DOM_DELTA_LINE ? e.deltaY * 32 : e.deltaY;
        factor = Math.exp(-deltaY * 0.0018);
      }
      setZoom((z) => {
        const raw = z * factor;
        const rounded = Math.round(raw * 100) / 100;
        return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, rounded));
      });
    };
    inner.addEventListener("wheel", onWheel, { passive: false });

    let pinchStartZoom = 1;
    const onGestureStart = (e: Event) => {
      e.preventDefault();
      pinchStartZoom = zoomRef.current;
    };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      const scale = (e as unknown as { scale: number }).scale;
      if (!Number.isFinite(scale) || scale <= 0) return;
      const next = pinchStartZoom * scale;
      const rounded = Math.round(next * 100) / 100;
      setZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, rounded)));
    };
    const onGestureEnd = (e: Event) => {
      e.preventDefault();
    };

    inner.addEventListener("gesturestart", onGestureStart, { passive: false });
    inner.addEventListener("gesturechange", onGestureChange, {
      passive: false,
    });
    inner.addEventListener("gestureend", onGestureEnd, { passive: false });

    return () => {
      ro.disconnect();
      inner.removeEventListener("wheel", onWheel);
      inner.removeEventListener("gesturestart", onGestureStart);
      inner.removeEventListener("gesturechange", onGestureChange);
      inner.removeEventListener("gestureend", onGestureEnd);
    };
  }, [isOpen, workingSrc, measureBaseFit, phase]);

  useLayoutEffect(() => {
    if (phase !== "adjust") return;
    const img = imgRef.current;
    if (!img || !crop || img.width < 2 || img.height < 2) return;
    setCompletedPixelCrop(convertToPixelCrop(crop, img.width, img.height));
  }, [zoom, baseFitWidth, crop, phase]);

  const handleCropFrameReset = useCallback(() => {
    const img = imgRef.current;
    if (!img || img.width === 0) return;
    applyDefaultCropFromImg(img);
  }, [applyDefaultCropFromImg]);

  const handleReset = useCallback(() => {
    revokeList(ownedBlobUrlsRef.current);
    ownedBlobUrlsRef.current = [];
    setWorkingSrc(imageUrl);
    setZoom(1);
    setBaseFitWidth(null);
    setCrop(undefined);
    setCompletedPixelCrop(null);
    requestAnimationFrame(() => {
      measureBaseFit();
      requestAnimationFrame(() => {
        const img = imgRef.current;
        if (img?.complete && img.naturalWidth > 0 && img.width > 0) {
          applyDefaultCropFromImg(img);
        }
      });
    });
  }, [applyDefaultCropFromImg, imageUrl, measureBaseFit]);

  const returnToAdjust = useCallback(() => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPreviewBlob(null);
    setPhase("adjust");
    handleReset();
  }, [handleReset]);

  const rotateBy = useCallback(async (delta: 90 | -90) => {
    const prev = workingSrcRef.current;
    try {
      const nextUrl = await rotateImageToBlobUrl(prev, delta);
      if (ownedBlobUrlsRef.current.includes(prev)) {
        URL.revokeObjectURL(prev);
        ownedBlobUrlsRef.current = ownedBlobUrlsRef.current.filter(
          (u) => u !== prev,
        );
      }
      ownedBlobUrlsRef.current.push(nextUrl);
      setZoom(1);
      setBaseFitWidth(null);
      setWorkingSrc(nextUrl);
      setCrop(undefined);
      setCompletedPixelCrop(null);
    } catch (e) {
      console.error("Rotate failed", e);
    }
  }, []);

  const onCropChange = useCallback(
    (_pixel: PixelCrop, percent: PercentCrop) => {
      setCrop(percent);
    },
    [],
  );

  const onCropComplete = useCallback((pixel: PixelCrop) => {
    setCompletedPixelCrop(pixel);
  }, []);

  /** Step 4.3 — export crop and show preview (step 4.4). */
  const handleDoneAdjust = useCallback(async () => {
    const img = imgRef.current;
    if (!img || !completedPixelCrop || exporting) return;
    if (completedPixelCrop.width < 2 || completedPixelCrop.height < 2) return;
    setExporting(true);
    try {
      let blob = await getCroppedImageBlobFromPixelCrop(
        img,
        completedPixelCrop,
      );
      blob = await downscaleBlobIfNeeded(blob, MAX_OUTPUT_EDGE);
      const url = URL.createObjectURL(blob);
      setPreviewBlob(blob);
      setPreviewUrl(url);
      setPhase("preview");
    } catch (e) {
      console.error("Crop export failed", e);
    } finally {
      setExporting(false);
    }
  }, [completedPixelCrop, exporting]);

  const goToSubjectStep = useCallback(() => {
    if (!previewBlob || !previewUrl) return;
    setPhase("subject");
  }, [previewBlob, previewUrl]);

  const backFromSubjectToPreview = useCallback(() => {
    setPhase("preview");
  }, []);

  const handleApplySubject = useCallback(async () => {
    if (
      !selectedSubject ||
      !previewBlob ||
      !previewUrl ||
      subjectApplyPending
    ) {
      return;
    }
    const id = Number(selectedSubject);
    const row = subjects.find((s) => s.subjectId === id);
    if (!row) return;
    previewCommittedRef.current = true;
    setSubjectApplyPending(true);
    try {
      await Promise.resolve(
        onComplete({
          blob: previewBlob,
          previewObjectUrl: previewUrl,
          subjectId: row.subjectId,
          subjectName: row.name,
        }),
      );
    } finally {
      setSubjectApplyPending(false);
    }
  }, [
    onComplete,
    previewBlob,
    previewUrl,
    selectedSubject,
    subjectApplyPending,
    subjects,
  ]);

  const imgStyle: CSSProperties | undefined =
    baseFitWidth != null
      ? {
          width: Math.max(1, Math.round(baseFitWidth * zoom)),
          maxWidth: "none",
          height: "auto",
        }
      : undefined;

  const adjustHeader = (
    <Box className="modal-header">
      <div className="aq-modal-title">{t("adjust_the_box_title")}</div>
      <button
        type="button"
        className="close-icon-wrapper"
        onClick={onCancel}
        aria-label={t("cancel")}
      >
        <Icon
          name="close"
          width={24}
          height={24}
          color={theme.colors.primary[40]}
        />
      </button>
    </Box>
  );

  const subjectHeader = (
    <Box className="modal-header">
      <div className="aq-modal-title">{t("select_subject_modal_title")}</div>
      <button
        type="button"
        className="close-icon-wrapper"
        onClick={backFromSubjectToPreview}
        disabled={subjectApplyPending}
        aria-label={t("cancel")}
      >
        <Icon
          name="close"
          width={24}
          height={24}
          color={theme.colors.primary[40]}
        />
      </button>
    </Box>
  );

  const adjustPreviewBody = (
    <div className="aq-modal-body aq-adjust-body">
      <div className="aq-adjust-crop-wrap">
        {phase === "adjust" ? (
          <div
            ref={innerRef}
            className="aq-adjust-react-crop-inner"
            aria-label={t("adjust_crop_stage_a11y")}
          >
            <ReactCrop
              crop={crop}
              ruleOfThirds
              keepSelection
              onChange={onCropChange}
              onComplete={onCropComplete}
            >
              <img
                ref={imgRef}
                key={workingSrc}
                src={workingSrc}
                alt=""
                className="aq-adjust-crop-img"
                style={imgStyle}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          </div>
        ) : phase === "preview" ? (
          <div
            className="aq-adjust-react-crop-inner aq-adjust-preview-inner"
            aria-label={t("adjust_preview_a11y")}
          >
            {previewUrl ? (
              <img src={previewUrl} alt="" className="aq-adjust-preview-img" />
            ) : null}
          </div>
        ) : null}
        <div className="aq-adjust-toolbar">
          <button
            type="button"
            className="aq-adjust-tool-btn"
            aria-label={t("crop_tool_a11y")}
            onClick={
              phase === "preview" ? returnToAdjust : handleCropFrameReset
            }
          >
            <img
              src={cropTipUrl}
              alt=""
              width={48}
              height={48}
              className="aq-adjust-tool-img-icon"
              aria-hidden
            />
          </button>
          <button
            type="button"
            className="aq-adjust-tool-btn"
            aria-label={t("rotate_right_a11y")}
            onClick={() =>
              phase === "preview" ? returnToAdjust() : void rotateBy(90)
            }
          >
            <img
              src={rightRotateUrl}
              alt=""
              width={48}
              height={48}
              className="aq-adjust-tool-img-icon"
              aria-hidden
            />
          </button>
          <button
            type="button"
            className="aq-adjust-tool-btn"
            aria-label={t("rotate_left_a11y")}
            onClick={() =>
              phase === "preview" ? returnToAdjust() : void rotateBy(-90)
            }
          >
            <img
              src={leftRotateUrl}
              alt=""
              width={48}
              height={48}
              className="aq-adjust-tool-img-icon"
              aria-hidden
            />
          </button>
          <button
            type="button"
            className="aq-adjust-reset"
            onClick={phase === "preview" ? returnToAdjust : handleReset}
          >
            {t("reset")}
          </button>
        </div>
      </div>
      <div className="aq-adjust-footer">
        <button
          type="button"
          className="aq-adjust-footer-cancel"
          onClick={onCancel}
        >
          {t("cancel")}
        </button>
        {phase === "adjust" ? (
          <button
            type="button"
            className="aq-adjust-footer-done"
            disabled={!completedPixelCrop || exporting}
            onClick={() => void handleDoneAdjust()}
          >
            {exporting ? t("processing") : t("adjust_done")}
          </button>
        ) : (
          <button
            type="button"
            className="aq-adjust-footer-done"
            disabled={!previewBlob}
            onClick={goToSubjectStep}
          >
            {t("select_subject_primary")}
          </button>
        )}
      </div>
    </div>
  );

  const selectSubjectBody = (
    <div className="aq-modal-body aq-modal-body--subject">
      {!sessionSubjectsReady ? (
        <Text as="p" className="aq-subject-section-label">
          {t("session_missing_subjects")}
        </Text>
      ) : subjectsLoading ? (
        <Text as="p" className="aq-subject-section-label">
          {t("subjects_loading")}
        </Text>
      ) : subjectsError ? (
        <div className="aq-btn-container aq-btn-container--subject-footer">
          <Text as="p" className="aq-subject-section-label">
            {t("subjects_load_error")}
          </Text>
          {onRetrySubjects ? (
            <button
              type="button"
              className="apply-btn"
              onClick={onRetrySubjects}
            >
              {t("subjects_retry")}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="aq-subject-radio-list">
          <RadioGroup
            name="doubts-browse-image-subject"
            value={selectedSubject}
            onChange={setSelectedSubject}
            gap="xl"
          >
            {subjects.map((row) => (
              <Radio
                key={row.subjectId}
                value={String(row.subjectId)}
                label={row.name}
                size="md"
              />
            ))}
          </RadioGroup>
        </div>
      )}
      <div className="aq-btn-container aq-btn-container--subject-footer">
        <button
          type="button"
          className="clear-btn"
          disabled={subjectApplyPending}
          onClick={backFromSubjectToPreview}
        >
          {t("cancel")}
        </button>
        <button
          type="button"
          className="apply-btn"
          disabled={
            subjectApplyPending ||
            !selectedSubject ||
            subjectsLoading ||
            subjectsError ||
            !sessionSubjectsReady
          }
          onClick={() => void handleApplySubject()}
        >
          {subjectApplyPending ? t("processing") : t("apply")}
        </button>
      </div>
    </div>
  );

  const handleSubjectModalOverlayClose = useCallback(() => {
    if (phase === "subject" && subjectApplyPending) return;
    if (phase === "subject") backFromSubjectToPreview();
    else onCancel();
  }, [backFromSubjectToPreview, onCancel, phase, subjectApplyPending]);

  return (
    <Modal
      isModalOpen={isOpen}
      onClose={handleSubjectModalOverlayClose}
      showFooter={false}
      width={phase === "subject" ? "min(36rem, 94vw)" : "55.938rem"}
      height={phase === "subject" ? "auto" : "45.75rem"}
      modalClassName={
        phase === "subject" ? "aq-adjust-modal--subject" : undefined
      }
      bodyClassName={
        phase === "subject" ? "aq-adjust-modal-body--subject" : undefined
      }
      customHeader={phase === "subject" ? subjectHeader : adjustHeader}
      body={phase === "subject" ? selectSubjectBody : adjustPreviewBody}
      alignItems="center"
      styles={{
        overlay: { zIndex: overlayZIndex },
      }}
    />
  );
}
